<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import Banner from 'next/banner/Banner.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const props = defineProps({
  enableCallingOnComplete: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showRestrictionAlert: {
    type: Boolean,
    default: false,
  },
  restrictionStatusUrl: {
    type: String,
    default: '',
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { isAuthenticating, runEmbeddedSignup } = useWhatsappEmbeddedSignup();

const isProcessing = ref(false);
const processingMessage = ref('');

const steps = [
  {
    title: 'Entre na sua conta Meta',
    description:
      'Use uma conta que tenha permissão para administrar o WhatsApp da sua empresa.',
    icon: 'i-lucide-user-round',
  },
  {
    title: 'Escolha a empresa e o número',
    description:
      'Siga as orientações da Meta para selecionar ou cadastrar os dados do seu negócio.',
    icon: 'i-lucide-building-2',
  },
  {
    title: 'Confirme e volte para a Talkvia',
    description:
      'Revise as permissões. Ao concluir, vamos preparar a conexão para o atendimento.',
    icon: 'i-lucide-check',
  },
];

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);

const enableCallingForInbox = async inboxId => {
  try {
    await InboxesAPI.enableWhatsappCalling(inboxId);
  } catch (_) {
    useAlert(
      t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CALLING_ENABLE_FAILED')
    );
  }
};

const handleSignupSuccess = async inboxData => {
  if (inboxData && inboxData.id) {
    if (props.enableCallingOnComplete) {
      isProcessing.value = true;
      processingMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.ENABLING_CALLING'
      );
      await enableCallingForInbox(inboxData.id);
    }
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inboxData.id,
      },
    });
  } else {
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
    router.replace({
      name: 'settings_inbox_list',
    });
  }
};

const launchEmbeddedSignup = async () => {
  if (props.isDisabled) return;

  let credentials;
  try {
    credentials = await runEmbeddedSignup();
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR')
    );
    return;
  }

  // Resolves null when the user dismisses the Meta popup.
  if (!credentials) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );
  try {
    const inboxData = await store.dispatch(
      'inboxes/createWhatsAppEmbeddedSignup',
      credentials
    );
    await handleSignupSuccess(inboxData);
  } catch (error) {
    isProcessing.value = false;
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="h-full">
    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else class="flex flex-col gap-5">
      <div class="mb-0 flex items-start gap-3">
        <div
          class="grid size-12 shrink-0 place-items-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <Icon icon="i-woot-whatsapp" class="size-7" />
        </div>
        <div class="min-w-0">
          <span
            class="mb-2 inline-flex rounded-full bg-n-brand/10 px-2.5 py-1 text-xs font-semibold text-n-brand"
            >Novo fluxo da Meta · v4</span
          >
          <h3 class="mb-2 text-xl font-semibold text-n-slate-12">
            Seu WhatsApp conectado à Talkvia
          </h3>
          <p class="mb-0 text-sm leading-6 text-n-slate-11">
            Conecte o número da sua empresa em uma janela oficial da Meta. Você
            acompanha cada etapa e escolhe o que autorizar.
          </p>
        </div>
      </div>
      <div class="m-0 rounded-xl border border-n-brand/20 bg-n-brand/5 p-4">
        <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
          O que ficou mais simples?
        </h4>
        <p class="mb-0 text-sm leading-6 text-n-slate-11">
          O v4 reúne as escolhas da empresa, do número e das permissões em
          etapas mais organizadas. Fica mais fácil entender o que está sendo
          conectado antes de confirmar.
        </p>
      </div>
      <ol class="!m-0 grid list-none gap-3 p-0">
        <li
          v-for="(step, index) in steps"
          :key="step.title"
          class="flex gap-3 rounded-xl border border-n-weak p-3"
        >
          <span
            class="grid size-7 shrink-0 place-items-center rounded-full bg-n-alpha-2 text-xs font-semibold text-n-slate-12"
            >{{ index + 1 }}</span
          >
          <div>
            <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
              {{ step.title }}
            </h4>
            <p class="mb-0 text-sm leading-5 text-n-slate-11">
              {{ step.description }}
            </p>
          </div>
        </li>
      </ol>
      <details
        class="m-0 rounded-xl border border-n-weak p-3 text-sm text-n-slate-11"
      >
        <summary class="cursor-pointer font-medium text-n-slate-12">
          Já uso o WhatsApp Business no celular. Posso continuar?
        </summary>
        <p class="mb-0 mt-2 leading-6">
          Quando a Meta disponibilizar essa opção para seu número, você poderá
          usar o aplicativo e a Talkvia juntos. A disponibilidade será informada
          durante a conexão.
        </p>
      </details>

      <Banner v-if="showRestrictionAlert" color="amber" class="w-full mb-6">
        <div class="flex items-start gap-3 text-start">
          <Icon
            icon="i-lucide-triangle-alert"
            class="flex-shrink-0 size-4 mt-0.5"
          />
          <span>
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.RESTRICTED_WARNING')
            }}
            <a
              v-if="restrictionStatusUrl"
              :href="restrictionStatusUrl"
              class="link underline"
              rel="noopener noreferrer nofollow"
              target="_blank"
            >
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.STATUS_LINK') }}
            </a>
          </span>
        </div>
      </Banner>

      <div class="flex mt-4">
        <NextButton
          :disabled="isAuthenticating || isDisabled"
          :is-loading="isAuthenticating"
          solid
          blue
          class="w-full"
          @click="launchEmbeddedSignup"
        >
          Conectar meu WhatsApp
        </NextButton>
      </div>
      <p class="mb-0 mt-3 text-center text-xs text-n-slate-11">
        Uma janela da Meta será aberta. A Talkvia não recebe sua senha.
      </p>
      <a
        href="/hc/manual/pt_BR/conexoes-e-canais"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-3 inline-flex text-sm font-medium text-n-brand underline"
        >Precisa de ajuda? Consulte o manual da Talkvia</a
      >
    </div>
  </div>
</template>
